/**
 * Date-only values (a calendar day with no time and no timezone), such as a
 * case's incident date. The API sends and accepts them as "YYYY-MM-DD".
 *
 * `new Date('2026-02-11')` parses that string as UTC midnight, which is the
 * previous evening anywhere west of UTC, so the day shifts back by one when it
 * is shown or saved with local getters (ZLAYT-1405). These helpers never go
 * through UTC.
 */

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/;

/** Parse "YYYY-MM-DD" into a Date at local midnight of that same day. */
export const parseDateOnly = (value?: string | null): Date | null => {
  if (!value) return null;
  const match = DATE_ONLY.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

/** Format a local Date as "YYYY-MM-DD" using its local calendar day. */
export const toDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Show "YYYY-MM-DD" as "MM/DD/YYYY" by rearranging the text, no Date involved. */
export const formatDateOnly = (value?: string | null): string => {
  if (!value) return '';
  const match = DATE_ONLY.exec(value.trim());
  if (!match) return value;
  return `${match[2]}/${match[3]}/${match[1]}`;
};

// A typed date: month, day and a four-digit year, either with separators
// ("2/4/2025", "02-14-2025", "02.14.2025") or as eight digits ("02142025").
const TYPED_WITH_SEPARATORS = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;
const TYPED_DIGITS_ONLY = /^(\d{2})(\d{2})(\d{4})$/;

/**
 * Read a date the user typed as month, day, year (US order) into a Date at
 * local midnight. Returns null for anything that is not a full, real calendar
 * day, so a half-typed or impossible value is never turned into some other
 * date (ZLAYT-1577: "02142025" used to be saved as 02/01/2026).
 */
export const parseTypedDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const text = value.trim();
  const match =
    TYPED_WITH_SEPARATORS.exec(text) ?? TYPED_DIGITS_ONLY.exec(text);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};
